import { useState } from 'react'

import { MeHead, MonthPicker, dayLabel, monthLabel, useMeData } from './parts'
import { SubmitOt } from './DelegateSubmit'

/* ============================================================================
   OT ของฉัน — `/me/ot`
   API: GET /api/me/ot?month=YYYY-MM · backend/src/routes/me.js → lib/ot.js

   แทนหน้า OTRequest.aspx ของ ot.simat.work **เฉพาะฝั่งอ่าน**

   ⚠️ ยังไม่มีปุ่มขอ OT โดยตั้งใจ — ระบบเดิมยังรันอยู่บน DB ตัวเดียวกัน
      ถ้าเปิดให้ขอจากสองที่พร้อมกันจะได้ใบซ้ำที่ไม่มีใครรู้ว่าอันไหนจริง
   ============================================================================ */

type Rate = { rate: number; minutes: number; hours: number; count?: number }

type Item = {
  source: string
  rowId: string
  otDate: string
  otShiftText: string
  isHoliday: boolean
  timeStart: string | null
  timeEnd: string | null
  startsNextDay: boolean
  endsNextDay: boolean
  hours: number
  estimated: boolean
  rate: number | null
  rates: Rate[]
  statusText: string
  statusGroup: string
  closed: boolean
  remark: string | null
}

type Data = {
  month: string
  employee: { empCode: string; empName: string | null }
  items: Item[]
  // ⚠️ byRate เป็น array ไม่ใช่ map ของ rate→hours (ตรวจจาก response จริง)
  //    ถ้าใช้ Object.entries จะได้ป้าย "x0" "x1" ซึ่งเป็นลำดับใน array ไม่ใช่เรต
  summary: { count: number; hours: number; byRate: Rate[] }
  monthsAvailable: string[]
}

/** สีป้ายสถานะ — อนุมัติแล้ว = เขียว · ไม่อนุมัติ = แดง · ที่เหลือ = รอ */
const toneOf = (group: string) =>
  group === 'approved' ? 'chip--ok' : group === 'rejected' ? 'chip--bad' : 'chip--warn'

export default function MyOt() {
  const [month, setMonth] = useState('')
  const { data, loading, error, reload } = useMeData<Data>(
    'ot',
    month ? `?month=${encodeURIComponent(month)}` : '',
  )

  return (
    <>
      <MeHead
        title="OT ของฉัน"
        meta={data ? `${data.employee.empName ?? ''} · ${data.employee.empCode}` : undefined}
        loading={loading}
        error={error}
        onReload={reload}
      >
        {data && (
          <MonthPicker
            value={data.month}
            months={data.monthsAvailable}
            onChange={setMonth}
            disabled={loading}
          />
        )}
      </MeHead>

      {data && <SubmitOt empName={data.employee.empName ?? null} onDone={reload} />}

      {data && (
        <section className="card">
          <div className="section-head">
            <div>
              <h2>
                {data.summary.hours} ชม.
                <span className="meta"> · {data.summary.count} รายการ</span>
              </h2>
              <p className="meta">{monthLabel(data.month)}</p>
            </div>
            {/* แยกตามเรต เพราะเงินที่ได้ต่างกัน — ชั่วโมงรวมอย่างเดียวบอกไม่ได้ */}
            <div className="chips">
              {data.summary.byRate.map((r) => (
                <span key={r.rate} className="chip">
                  x{r.rate} · {r.hours} ชม.
                </span>
              ))}
            </div>
          </div>

          {data.items.length === 0 ? (
            <p className="meta">ไม่มี OT ในเดือน{monthLabel(data.month)}</p>
          ) : (
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th>วันที่</th>
                    <th>ช่วง</th>
                    <th>เวลา</th>
                    <th className="num">ชั่วโมง</th>
                    <th className="num">เรต</th>
                    <th>สถานะ</th>
                    <th>หมายเหตุ</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((it) => (
                    <tr key={`${it.rowId}-${it.otDate}`}>
                      <td className="nowrap">
                        {dayLabel(it.otDate)}
                        {it.isHoliday && <span className="chip chip--warn">วันหยุด</span>}
                      </td>
                      <td>{it.otShiftText}</td>
                      <td className="nowrap">
                        {it.timeStart ?? '—'}
                        {it.startsNextDay && <span className="chip">+1</span>}
                        {' – '}
                        {it.timeEnd ?? '—'}
                        {it.endsNextDay && <span className="chip">+1</span>}
                      </td>
                      <td className="num">
                        {it.hours}
                        {/* ใบที่ยังไม่ปิดงวด ชั่วโมงยังขยับได้ ต้องบอกให้รู้ */}
                        {it.estimated && <span className="meta"> ประมาณ</span>}
                      </td>
                      <td className="num">{it.rate === null ? '—' : `x${it.rate}`}</td>
                      <td className="nowrap">
                        <span className={`chip ${toneOf(it.statusGroup)}`}>{it.statusText}</span>
                        {it.closed && <span className="meta"> ปิดงวดแล้ว</span>}
                      </td>
                      <td className="cell-clip">{it.remark ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <p className="meta">
            ขอ OT ใหม่ยังต้องทำที่ระบบเดิม — หน้านี้ดูรายการและสถานะที่อนุมัติแล้ว
          </p>
        </section>
      )}
    </>
  )
}
